/**
 * Export content to various file formats
 * Supports Plain Text, Markdown, and HTML exports
 * PDF and Word support planned for Phase 2
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';
import html2pdf from 'html2pdf.js';

/**
 * Configure marked for safe HTML rendering
 */
const configureMarked = () => {
  marked.setOptions({
    gfm: true,
    breaks: true,
  });
};

// Initialize marked configuration
configureMarked();

/**
 * Export options for customizing file generation
 */
export interface ExportOptions {
  filename?: string;           // Custom filename (without extension)
  title?: string;              // Document title for HTML/PDF
  includeTimestamp?: boolean;  // Add timestamp to filename (default: true)
}

/**
 * Supported export formats
 */
export type ExportFormat = 'text' | 'markdown' | 'html' | 'pdf' | 'word';

/**
 * Format metadata for UI display
 */
export interface FormatInfo {
  id: ExportFormat;
  label: string;
  extension: string;
  mimeType: string;
  available: boolean;
  icon: string;  // MUI icon name
}

/**
 * Available export formats with metadata
 */
export const EXPORT_FORMATS: FormatInfo[] = [
  { id: 'text', label: 'Plain Text', extension: 'txt', mimeType: 'text/plain', available: true, icon: 'TextSnippet' },
  { id: 'markdown', label: 'Markdown', extension: 'md', mimeType: 'text/markdown', available: true, icon: 'Code' },
  { id: 'html', label: 'HTML', extension: 'html', mimeType: 'text/html', available: true, icon: 'Html' },
  { id: 'pdf', label: 'PDF', extension: 'pdf', mimeType: 'application/pdf', available: true, icon: 'PictureAsPdf' },
  { id: 'word', label: 'Word', extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', available: true, icon: 'Article' },
];

/**
 * Convert markdown to clean HTML
 */
function markdownToHtml(markdown: string): string {
  const rawHtml = marked.parse(markdown) as string;
  
  const cleanHtml = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'del',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'hr', 'div', 'span', 'img', 'sup', 'sub'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class']
  });
  
  return cleanHtml;
}

/**
 * Convert markdown to plain text by stripping formatting
 */
function markdownToPlainText(markdown: string): string {
  let text = markdown;
  
  // Remove code blocks but keep content
  text = text.replace(/```[\s\S]*?```/g, (match) => {
    const content = match.replace(/```\w*\n?/g, '').replace(/```$/g, '');
    return content.trim();
  });
  
  // Remove inline code backticks
  text = text.replace(/`([^`]+)`/g, '$1');
  
  // Convert headers to plain text with newlines
  text = text.replace(/^#{1,6}\s+(.+)$/gm, '$1');
  
  // Remove bold/italic markers
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');
  text = text.replace(/_([^_]+)_/g, '$1');
  
  // Convert links to just text (or text with URL)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
  
  // Convert unordered list markers
  text = text.replace(/^\s*[-*+]\s+/gm, '• ');
  
  // Convert ordered list markers (keep numbers)
  text = text.replace(/^\s*(\d+)\.\s+/gm, '$1. ');
  
  // Remove blockquote markers
  text = text.replace(/^\s*>\s*/gm, '');
  
  // Remove horizontal rules
  text = text.replace(/^[-*_]{3,}$/gm, '─'.repeat(40));
  
  // Clean up excessive newlines
  text = text.replace(/\n{3,}/g, '\n\n');
  
  return text.trim();
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Generate a sanitized filename
 */
function generateFilename(options: ExportOptions, extension: string): string {
  let baseName = options.filename || 'bolt-export';
  
  // Sanitize filename - remove invalid characters
  baseName = baseName
    .replace(/[<>:"/\\|?*]/g, '-')  // Replace invalid Windows chars
    .replace(/\s+/g, '-')           // Replace spaces with dashes
    .replace(/-+/g, '-')            // Collapse multiple dashes
    .replace(/^-|-$/g, '')          // Remove leading/trailing dashes
    .substring(0, 50);              // Limit length
  
  if (options.includeTimestamp !== false) {
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return `${baseName}-${timestamp}.${extension}`;
  }
  
  return `${baseName}.${extension}`;
}

/**
 * Trigger browser download for a blob
 */
function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get embedded CSS styles for HTML export
 */
function getExportStyles(): string {
  return `
    /* Reset and base styles */
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #333;
      background: #fff;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
    }
    
    /* Header styling */
    .export-header {
      border-bottom: 2px solid #1976d2;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    
    .export-header h1 {
      font-size: 24px;
      color: #1976d2;
      margin: 0 0 4px 0;
    }
    
    .export-header .meta {
      font-size: 12px;
      color: #666;
    }
    
    /* Content styling */
    .export-content {
      line-height: 1.7;
    }
    
    h1, h2, h3, h4, h5, h6 {
      color: #1a1a1a;
      margin-top: 24px;
      margin-bottom: 12px;
      font-weight: 600;
    }
    
    h1 { font-size: 24px; }
    h2 { font-size: 20px; }
    h3 { font-size: 18px; }
    h4 { font-size: 16px; }
    h5 { font-size: 14px; }
    h6 { font-size: 14px; color: #666; }
    
    p {
      margin: 12px 0;
    }
    
    /* Code styling */
    code {
      background: #f5f5f5;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 13px;
    }
    
    pre {
      background: #1e1e1e;
      color: #d4d4d4;
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin: 16px 0;
    }
    
    pre code {
      background: none;
      padding: 0;
      color: inherit;
      font-size: 13px;
    }
    
    /* Blockquote */
    blockquote {
      border-left: 4px solid #1976d2;
      margin: 16px 0;
      padding: 8px 16px;
      background: #f9f9f9;
      color: #555;
    }
    
    /* Lists */
    ul, ol {
      margin: 12px 0;
      padding-left: 24px;
    }
    
    li {
      margin: 6px 0;
    }
    
    /* Tables */
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 16px 0;
    }
    
    th, td {
      border: 1px solid #ddd;
      padding: 10px 12px;
      text-align: left;
    }
    
    th {
      background: #f5f5f5;
      font-weight: 600;
    }
    
    tr:nth-child(even) {
      background: #fafafa;
    }
    
    /* Links */
    a {
      color: #1976d2;
      text-decoration: none;
    }
    
    a:hover {
      text-decoration: underline;
    }
    
    /* Horizontal rule */
    hr {
      border: none;
      border-top: 1px solid #eee;
      margin: 24px 0;
    }
    
    /* Images */
    img {
      max-width: 100%;
      height: auto;
    }
    
    /* Footer */
    .export-footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #eee;
      font-size: 11px;
      color: #999;
      text-align: center;
    }
  `.trim();
}

/**
 * Export content as Plain Text (.txt)
 */
export async function exportAsText(content: string, options: ExportOptions = {}): Promise<void> {
  const plainText = markdownToPlainText(content);
  const filename = generateFilename(options, 'txt');
  const blob = new Blob([plainText], { type: 'text/plain;charset=utf-8' });
  downloadFile(blob, filename);
}

/**
 * Export content as Markdown (.md)
 */
export async function exportAsMarkdown(content: string, options: ExportOptions = {}): Promise<void> {
  const filename = generateFilename(options, 'md');
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  downloadFile(blob, filename);
}

/**
 * Export content as HTML (.html)
 */
export async function exportAsHtml(content: string, options: ExportOptions = {}): Promise<void> {
  const title = options.title || 'Bolt Export';
  const htmlContent = markdownToHtml(content);
  const exportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(title)}</title>
  <style>
${getExportStyles()}
  </style>
</head>
<body>
  <header class="export-header">
    <h1>${escapeHtml(title)}</h1>
    <p class="meta">Exported from Bolt • ${exportDate}</p>
  </header>
  
  <main class="export-content">
${htmlContent}
  </main>
  
  <footer class="export-footer">
    Generated by Bolt (Bossard AI Platform)
  </footer>
</body>
</html>`;

  const filename = generateFilename(options, 'html');
  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
  downloadFile(blob, filename);
}

/**
 * Export content as PDF (.pdf)
 * Uses html2pdf.js to convert HTML to PDF in the browser
 */
export async function exportAsPdf(content: string, options: ExportOptions = {}): Promise<void> {
  const title = options.title || 'Bolt Export';
  const htmlContent = markdownToHtml(content);
  const exportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  // Create a temporary container for PDF generation
  const container = document.createElement('div');
  container.innerHTML = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; max-width: 100%;">
      <header style="border-bottom: 2px solid #1976d2; padding-bottom: 16px; margin-bottom: 24px;">
        <h1 style="font-size: 24px; color: #1976d2; margin: 0 0 4px 0;">${escapeHtml(title)}</h1>
        <p style="font-size: 12px; color: #666; margin: 0;">Exported from Bolt • ${exportDate}</p>
      </header>
      <main style="font-size: 14px; line-height: 1.6; color: #333;">
        ${htmlContent}
      </main>
      <footer style="margin-top: 40px; padding-top: 16px; border-top: 1px solid #eee; font-size: 11px; color: #999; text-align: center;">
        Generated by Bolt (Bossard AI Platform)
      </footer>
    </div>
  `;
  
  // Apply inline styles for PDF rendering (html2pdf works better with inline styles)
  applyPdfStyles(container);
  
  const filename = generateFilename(options, 'pdf');
  
  // Configure html2pdf options
  const pdfOptions = {
    margin: [10, 10, 10, 10],
    filename: filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      letterRendering: true
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' as const
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };
  
  // Generate and download PDF
  await html2pdf().set(pdfOptions).from(container).save();
}

/**
 * Apply inline styles to elements for better PDF rendering
 */
function applyPdfStyles(container: HTMLElement): void {
  // Style code blocks
  container.querySelectorAll('pre').forEach(pre => {
    (pre as HTMLElement).style.cssText = 'background: #1e1e1e; color: #d4d4d4; padding: 16px; border-radius: 8px; overflow-x: auto; margin: 16px 0; font-family: Consolas, Monaco, monospace; font-size: 13px;';
  });
  
  container.querySelectorAll('code').forEach(code => {
    if (code.parentElement?.tagName !== 'PRE') {
      (code as HTMLElement).style.cssText = 'background: #f5f5f5; padding: 2px 6px; border-radius: 4px; font-family: Consolas, Monaco, monospace; font-size: 13px;';
    }
  });
  
  // Style headings
  container.querySelectorAll('h1').forEach(h => {
    (h as HTMLElement).style.cssText = 'font-size: 20px; color: #1a1a1a; margin-top: 24px; margin-bottom: 12px; font-weight: 600;';
  });
  container.querySelectorAll('h2').forEach(h => {
    (h as HTMLElement).style.cssText = 'font-size: 18px; color: #1a1a1a; margin-top: 20px; margin-bottom: 10px; font-weight: 600;';
  });
  container.querySelectorAll('h3').forEach(h => {
    (h as HTMLElement).style.cssText = 'font-size: 16px; color: #1a1a1a; margin-top: 16px; margin-bottom: 8px; font-weight: 600;';
  });
  
  // Style blockquotes
  container.querySelectorAll('blockquote').forEach(bq => {
    (bq as HTMLElement).style.cssText = 'border-left: 4px solid #1976d2; margin: 16px 0; padding: 8px 16px; background: #f9f9f9; color: #555;';
  });
  
  // Style tables
  container.querySelectorAll('table').forEach(table => {
    (table as HTMLElement).style.cssText = 'border-collapse: collapse; width: 100%; margin: 16px 0;';
  });
  container.querySelectorAll('th, td').forEach(cell => {
    (cell as HTMLElement).style.cssText = 'border: 1px solid #ddd; padding: 10px 12px; text-align: left;';
  });
  container.querySelectorAll('th').forEach(th => {
    (th as HTMLElement).style.cssText += 'background: #f5f5f5; font-weight: 600;';
  });
  
  // Style lists
  container.querySelectorAll('ul, ol').forEach(list => {
    (list as HTMLElement).style.cssText = 'margin: 12px 0; padding-left: 24px;';
  });
  container.querySelectorAll('li').forEach(li => {
    (li as HTMLElement).style.cssText = 'margin: 6px 0;';
  });
  
  // Style paragraphs
  container.querySelectorAll('p').forEach(p => {
    (p as HTMLElement).style.cssText = 'margin: 12px 0;';
  });
  
  // Style links
  container.querySelectorAll('a').forEach(a => {
    (a as HTMLElement).style.cssText = 'color: #1976d2; text-decoration: none;';
  });
}

/**
 * Export content as Word (.docx)
 * Uses backend API with python-docx for Word document generation
 */
export async function exportAsWord(content: string, options: ExportOptions = {}): Promise<void> {
  const title = options.title || 'Bolt Export';
  const filename = options.filename || generateFilename(options, 'docx').replace('.docx', '');
  
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    const response = await fetch('/api/export/word', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        content,
        title,
        filename,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${response.status} - ${errorText}`);
    }
    
    // Get the blob from response
    const blob = await response.blob();
    
    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let downloadFilename = `${filename}.docx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        downloadFilename = filenameMatch[1];
      }
    }
    
    // Download the file
    downloadFile(blob, downloadFilename);
  } catch (error) {
    console.error('Word export error:', error);
    throw new Error(`Failed to export Word document: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Universal export function - routes to appropriate format handler
 */
export async function exportToFile(
  content: string, 
  format: ExportFormat, 
  options: ExportOptions = {}
): Promise<void> {
  switch (format) {
    case 'text':
      return exportAsText(content, options);
    case 'markdown':
      return exportAsMarkdown(content, options);
    case 'html':
      return exportAsHtml(content, options);
    case 'pdf':
      return exportAsPdf(content, options);
    case 'word':
      return exportAsWord(content, options);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}

/**
 * Check if a format is currently available
 */
export function isFormatAvailable(format: ExportFormat): boolean {
  const formatInfo = EXPORT_FORMATS.find(f => f.id === format);
  return formatInfo?.available ?? false;
}

/**
 * Get format info by id
 */
export function getFormatInfo(format: ExportFormat): FormatInfo | undefined {
  return EXPORT_FORMATS.find(f => f.id === format);
}
